import { Button } from 'primereact/button';
import classes from './GridSection.module.scss';
import { useGridSection } from './useGridSection.logic';
import checkIcon from 'assets/icons/checkIcon.svg';
import saveIcon from 'assets/icons/saveIcon.svg';
import commentIcon from 'assets/icons/commentIcon.svg';
import { InputText } from 'primereact/inputtext';
import GridSkeletalLoader from './components/GridSectionSkeleton';
import NotesSection from './components/NotesSection/NotesSection';
import InfiniteScroll from 'react-infinite-scroller';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  salesPlanBulkEditDataInterface,
  msPlanBulkEditFilterInterface,
  notesInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';
import BulkEditNoData from 'components/BulkEditNoData/BulkEditNoData';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Menu } from 'primereact/menu';
import { InputSwitch } from 'primereact/inputswitch';

// Define sales plan type
type SalesPlanType = 'current' | 'old' | 'saved';

const GridSection = ({
  dataLoading,
  data,
  getForecastValue,
  handleSaveInputValue,
  isSaveButtonEnabled,
  isSaveAllBtnEnabled,
  gfcCotSCNotesErrorSet,
  handleSingleSave,
  handleSaveAllEdited,
  checkGfcCotSCisSaving,
  isNotesButtonEnabled,
  handleNoteButtonClick,
  loadMore,
  hasMore,
  nextPageLoading,
  filterData,
  isNotesLoading,
  notesData,
  isNoteSaving,
  handleAddNote,
  toggleVisibilityForNotes,
  focussedRowForNoteAdd,
  setNotesData,
  isNoteEdited,
  setIsNoteEdited,
  isNoteDeleted,
  setIsNoteDeleted,
  handleCopy,
  GFCNotesSet: _GFCNotesSet,
  handleEditNote,
  handleDeleteNote,
  deletingNote,
  isNoteDeleting,
  isDeleteClicked,
  setIsDeleteClicked,
  setDeletingNote,
  initialLoading,
  // New accordion-related props
  expandedRows: _expandedRows,
  toggleRowExpansion,
  isRowExpanded,
  getActiveMarketForecastValue,
  // New concept sort prop
  handleConceptSort,
  // New sales plan type selection props
  getSelectedSalesPlanType,
  handleSalesPlanTypeChange,
  getSalesPlanValueByType: _getSalesPlanValueByType,
  shouldShowToggle,
}: {
  dataLoading: boolean;
  data: salesPlanBulkEditDataInterface[];
  initialLoading: boolean;
  getForecastValue: (rowIndex: number, period: string) => string;
  handleSaveInputValue: (rowIndex: number, period: string, adjustment: string) => void;
  isSaveButtonEnabled: (rowIndex: number) => boolean;
  isSaveAllBtnEnabled: () => boolean;
  gfcCotSCNotesErrorSet: Set<string>;
  handleSingleSave: (idx: number) => void;
  handleSaveAllEdited: () => void;
  checkGfcCotSCisSaving: (idx: number) => boolean;
  isSaveAllLoading: boolean;
  isNotesButtonEnabled: (rowIndex: number) => boolean;
  handleNoteButtonClick: (rowIndex: number) => void;
  hasMore: boolean;
  nextPageLoading: boolean;
  loadMore: () => void;
  filterData: msPlanBulkEditFilterInterface;
  visibility: boolean;
  handleCancel: () => void;
  isNotesLoading: boolean;
  notesData: notesInterface[] | undefined;
  isNoteSaving: boolean;
  handleAddNote: (value: string) => Promise<boolean>;
  toggleVisibilityForNotes: () => void;
  focussedRowForNoteAdd: number | null;
  setNotesData: Dispatch<SetStateAction<notesInterface[] | undefined>>;
  isNoteEdited: Map<number, boolean>;
  setIsNoteEdited: Dispatch<SetStateAction<Map<number, boolean>>>;
  isNoteDeleted: Map<number, boolean>;
  setIsNoteDeleted: Dispatch<SetStateAction<Map<number, boolean>>>;
  handleCopy: (idx: number, month: string) => void;
  GFCNotesSet: Set<string>;
  handleEditNote: (id: number, note: string) => Promise<void>;
  handleDeleteNote: (id: number) => Promise<void>;
  deletingNote: notesInterface | null;
  isNoteDeleting: boolean;
  isDeleteClicked: boolean;
  setIsDeleteClicked: Dispatch<SetStateAction<boolean>>;
  setDeletingNote: Dispatch<SetStateAction<notesInterface | null>>;
  // New accordion-related props
  expandedRows: Set<number>;
  toggleRowExpansion: (rowIndex: number) => void;
  isRowExpanded: (rowIndex: number) => boolean;
  getActiveMarketForecastValue: (rowIndex: number, monthDateString: string) => string;
  // New concept sort prop
  handleConceptSort: (sortType: 'asc' | 'desc' | '') => void;
  // New sales plan type selection props
  getSelectedSalesPlanType: (rowIndex: number) => SalesPlanType;
  handleSalesPlanTypeChange: (rowIndex: number, type: SalesPlanType) => void;
  getSalesPlanValueByType: (
    rowIndex: number,
    monthDateString: string,
    type: SalesPlanType
  ) => string;
  shouldShowToggle: (rowIndex: number) => boolean;
}) => {
  const { forecastingMonthsEdit } = useGridSection();
  const tableRef = useRef<HTMLDivElement>(null);
  const [isCopyDotVisible, setIsCopyDotVisible] = useState<Set<string>>(new Set());
  const sortOverlayRef = useRef<OverlayPanel>(null);

  // Function to get sort icon
  const getSortIcon = (sortType: string): string => {
    switch (sortType) {
      case 'asc':
        return 'pi pi-sort-up';
      case 'desc':
        return 'pi pi-sort-down';
      default:
        return 'pi pi-sort';
    }
  };

  // Function to get sort label
  const getSortLabel = (sortType: string): string => {
    switch (sortType) {
      case 'asc':
        return 'Ascending';
      case 'desc':
        return 'Descending';
      default:
        return 'No Sorting';
    }
  };

  // Sort menu items
  const getSortMenuItems = () => {
    const currentSort = filterData.ConceptSort;
    return [
      {
        label: 'Ascending',
        icon: 'pi pi-sort-up',
        disabled: currentSort === 'asc',
        command: () => {
          handleConceptSort('asc');
          sortOverlayRef.current?.hide();
        },
      },
      {
        label: 'Descending',
        icon: 'pi pi-sort-down',
        disabled: currentSort === 'desc',
        command: () => {
          handleConceptSort('desc');
          sortOverlayRef.current?.hide();
        },
      },
      {
        label: 'Remove Sorting',
        icon: 'pi pi-times',
        disabled: currentSort === '',
        command: () => {
          handleConceptSort('');
          sortOverlayRef.current?.hide();
        },
      },
    ];
  };

  return (
    <>
      {dataLoading ? (
        <GridSkeletalLoader />
      ) : (
        <>
          <div className={classes.container} ref={tableRef} key={JSON.stringify(filterData)}>
            <table className={classes.tableStyling}>
              <thead className={classes.tableHeading}>
                <th
                  className={`${classes.thContent} ${classes.stickyExpand}`}
                  style={{ borderTopLeftRadius: '8px' }}
                >
                  {/* Expand/Collapse column */}
                </th>
                <th className={`${classes.thContent} ${classes.stickyConcept}`}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Concept</span>
                    <Button
                      icon={getSortIcon(filterData.ConceptSort)}
                      className="p-button-text p-button-sm"
                      onClick={e => sortOverlayRef.current?.toggle(e)}
                      style={{
                        fontSize: '12px',
                        padding: '0.25rem',
                        minWidth: 'auto',
                        height: 'auto',
                        color: 'white',
                      }}
                      tooltip={`Current: ${getSortLabel(filterData.ConceptSort)}`}
                      tooltipOptions={{ position: 'top' }}
                    />
                    <OverlayPanel ref={sortOverlayRef} className="p-overlaypanel-sm">
                      <Menu model={getSortMenuItems()} />
                    </OverlayPanel>
                  </div>
                </th>
                <th className={`${classes.thContent} ${classes.stickyGfc}`}>
                  GFC
                </th>
                <th className={`${classes.thContent} ${classes.stickyCot}`}>
                  COT
                </th>
                <th className={`${classes.thContent} ${classes.stickySellCat}`}>
                  Selling Category
                </th>
                <th className={`${classes.thContent} ${classes.stickyType}`}>
                  Type
                </th>
                {forecastingMonthsEdit.map((item, idx) => {
                  return (
                    <th
                      className={classes.thContent}
                      style={{
                        borderTopRightRadius: `${idx === 11 ? '8px' : ''}`,
                      }}
                    >
                      {item.label}
                    </th>
                  );
                })}
                <th className={classes.saveAllButtonHeading}>
                  <Button
                    className={classes.saveAllBtn}
                    disabled={!isSaveAllBtnEnabled()}
                    onClick={handleSaveAllEdited}
                    // loading={isSaveAllLoading}
                  >
                    <img
                      src={checkIcon}
                      alt="check"
                      style={{
                        width: '36px',
                        height: '30px',
                        paddingRight: '1rem',
                      }}
                    />
                    Save All
                  </Button>
                </th>
              </thead>
              <InfiniteScroll
                element="tbody"
                pageStart={0}
                loadMore={loadMore}
                hasMore={hasMore}
                useWindow={false}
                getScrollParent={() => tableRef.current}
                // initialLoad={false} // prevent auto-dataLoading on mount
                threshold={100}
                className={classes.tableBody}
              >
                {data &&
                  data.map((item, idx) => {
                    const isExpanded = isRowExpanded(idx);
                    const selectedType = getSelectedSalesPlanType(idx);

                    return (
                      <>
                        <tr
                          className={classes.trContent}
                          style={{
                            backgroundColor: `${idx % 2 ? '#F7F3FE' : '#FFFFFF'}`,
                          }}
                        >
                          {/* Expand/Collapse button */}
                          <td className={classes.stickyExpand} style={{ textAlign: 'center' }}>
                            <Button
                              icon={isExpanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
                              className="p-button-text p-button-sm"
                              onClick={() => toggleRowExpansion(idx)}
                              style={{ fontSize: '12px', padding: '0.25rem' }}
                            />
                          </td>
                          <td
                            className={classes.stickyConcept}
                            style={{
                              fontSize: '11px',
                              backgroundColor: `${
                                gfcCotSCNotesErrorSet.has(
                                  `${item.GFC}-${item.COT}-${item.SellingCategory}`
                                )
                                  ? '#fbbaba'
                                  : ''
                              }`,
                            }}
                          >
                            {item.Concept}
                          </td>
                          <td
                            className={classes.stickyGfc}
                            style={{
                              backgroundColor: `${
                                gfcCotSCNotesErrorSet.has(
                                  `${item.GFC}-${item.COT}-${item.SellingCategory}`
                                )
                                  ? '#fbbaba'
                                  : ''
                              }`,
                              fontSize: '12px',
                            }}
                          >
                            {item.GFC}
                          </td>
                          <td
                            className={classes.stickyCot}
                            style={{
                              backgroundColor: `${
                                gfcCotSCNotesErrorSet.has(
                                  `${item.GFC}-${item.COT}-${item.SellingCategory}`
                                )
                                  ? '#fbbaba'
                                  : ''
                              }`,
                              fontSize: '12px',
                            }}
                          >
                            {item.COT.toUpperCase()}
                          </td>
                          <td
                            className={classes.stickySellCat}
                            style={{
                              backgroundColor: `${
                                gfcCotSCNotesErrorSet.has(
                                  `${item.GFC}-${item.COT}-${item.SellingCategory}`
                                )
                                  ? '#fbbaba'
                                  : ''
                              }`,
                              fontSize: '12px',
                            }}
                          >
                            {item.SellingCategory.charAt(0).toUpperCase() +
                              item.SellingCategory.slice(1)}
                          </td>
                          {/* Sales Plan Type Toggle Column */}
                          <td className={classes.stickyType} style={{ textAlign: 'center' }}>
                            {shouldShowToggle(idx) ? (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '0.5rem',
                                }}
                              >
                                <span style={{ fontSize: '10px', color: '#6b7280' }}>Current</span>
                                <InputSwitch
                                  checked={selectedType === 'old'}
                                  onChange={e =>
                                    handleSalesPlanTypeChange(idx, e.value ? 'old' : 'current')
                                  }
                                  style={{ transform: 'scale(0.8)' }}
                                />
                                <span style={{ fontSize: '10px', color: '#6b7280' }}>Old</span>
                              </div>
                            ) : (
                              // Show the current type when toggle conditions are not met
                              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                {selectedType === 'saved'
                                  ? 'Saved'
                                  : selectedType === 'old'
                                    ? 'Old'
                                    : 'Current'}
                              </span>
                            )}
                          </td>
                          {forecastingMonthsEdit.map((month, mnt_idx) => {
                            const spEntry = item.data.find(d => d.PlanningMonth === month.dateString);
                            const derampTitle = spEntry?.is_deramped === true
                              ? `NC De-ramp applied — contract term date: ${month.dateString}`
                              : undefined;
                            return (
                              <td style={{ position: 'relative' }} title={derampTitle}>
                                <InputText
                                  className={classes.input}
                                  value={getForecastValue(idx, month.dateString)}
                                  onChange={e =>
                                    handleSaveInputValue(idx, month.dateString, e.target.value.replace(/,/g, ''))
                                  }
                                  onFocus={() =>
                                    setIsCopyDotVisible(() => {
                                      const temp = new Set('');
                                      temp.add(
                                        item.GFC.toString() + idx.toString() + month.dateString
                                      );
                                      return temp;
                                    })
                                  }
                                />
                                {isCopyDotVisible.has(
                                  item.GFC.toString() + idx.toString() + month.dateString
                                ) &&
                                  mnt_idx < 11 && (
                                    <span
                                      title="Copy value in all cells to the right of current row"
                                      className={classes.copyPointerBtn}
                                      onClick={() => {
                                        handleCopy(idx, month.dateString);
                                        setIsCopyDotVisible(new Set());
                                      }}
                                      style={{
                                        position: 'absolute',
                                        bottom: '-1px',
                                        right: '3px',
                                      }}
                                    >
                                      .
                                    </span>
                                  )}
                              </td>
                            );
                          })}

                          <td className={classes.stickySave}>
                            <div className={classes.btnContainer}>
                              <Button
                                className={classes.saveBtn}
                                disabled={!isSaveButtonEnabled(idx)}
                                onClick={() => handleSingleSave(idx)}
                                loading={checkGfcCotSCisSaving(idx)}
                              >
                                {' '}
                                <img
                                  src={saveIcon}
                                  alt="save"
                                  style={{ paddingRight: '1rem' }}
                                />{' '}
                                Save
                              </Button>
                              <Button
                                className={classes.commentBtn}
                                disabled={!isNotesButtonEnabled(idx)}
                                onClick={() => handleNoteButtonClick(idx)}
                              >
                                <img src={commentIcon} alt="comment" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded row showing ActiveMarketForecast values */}
                        {isExpanded && (
                          <tr
                            style={{
                              backgroundColor: '#f8f9fa',
                              borderTop: '1px solid #e9ecef',
                            }}
                          >
                            <td className={classes.stickyExpand}></td> {/* Empty cell for expand/collapse column */}
                            <td
                              colSpan={5}
                              className={classes.stickyFixedColumns}
                              style={{
                                padding: '0.5rem',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                textAlign: 'right',
                              }}
                            >
                              Active Market Forecast Values:
                            </td>
                            {forecastingMonthsEdit.map(month => (
                              <td
                                style={{
                                  paddingLeft: '0.7rem',
                                  fontSize: '11px',
                                  textAlign: 'left',
                                }}
                              >
                                {getActiveMarketForecastValue(idx, month.dateString)}
                              </td>
                            ))}
                            <td className={classes.stickySave}></td> {/* Empty cell for action buttons column */}
                          </tr>
                        )}
                      </>
                    );
                  })}
                {(nextPageLoading || dataLoading) && (
                  <tr>
                    {forecastingMonthsEdit.map((_, idx) => {
                      if (idx === 8) {
                        return (
                          <td>
                            <ProgressSpinner
                              strokeWidth=""
                              style={{ width: '3.5rem', height: '5rem' }}
                            />
                          </td>
                        );
                      } else {
                        return <td></td>;
                      }
                    })}
                  </tr>
                )}
              </InfiniteScroll>
            </table>

            {!initialLoading && data.length === 0 && !nextPageLoading && (
              <BulkEditNoData
                label={'No Sales Plan data available for the applied filter values!!'}
              />
            )}
          </div>
          <NotesSection
            visibility={focussedRowForNoteAdd !== null}
            handleCancel={toggleVisibilityForNotes}
            isNotesLoading={isNotesLoading}
            notesData={notesData}
            setNotesData={setNotesData}
            isNoteSaving={isNoteSaving}
            handleAddNote={handleAddNote}
            isNoteEdited={isNoteEdited}
            setIsNoteEdited={setIsNoteEdited}
            isNoteDeleted={isNoteDeleted}
            setIsNoteDeleted={setIsNoteDeleted}
            handleEditNote={handleEditNote}
            handleDeleteNote={handleDeleteNote}
            deletingNote={deletingNote}
            isNoteDeleting={isNoteDeleting}
            isDeleteClicked={isDeleteClicked}
            setIsDeleteClicked={setIsDeleteClicked}
            setDeletingNote={setDeletingNote}
          />
        </>
      )}
    </>
  );
};

export default GridSection;
